import React from 'react';
import { creativesServiceConfigured } from './creative-api';
import { fetchAboveTheFold, hasAboveTheFold, type AboveTheFold, type AboveTheFoldRead } from './above-the-fold-api';

/** `idle`: nothing to read with (no brand, no site host, no creatives service), so nothing shows. */
export type AboveTheFoldStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface AboveTheFoldState {
  status: AboveTheFoldStatus;
  /** The answer, once the read for the current brand and host is in. */
  atf: AboveTheFold | null;
  /** Read it again (after a failed read). */
  reload: () => void;
}

/** The last answer: which read it answered (`key`: brand, host and attempt) and for which brand and host. */
export type AboveTheFoldLast = { key: string; brandKey: string; read: AboveTheFoldRead };

/**
 * The status of the read for `key` (of `brandKey`), from whether one is asked for and the last answer. A re-read of
 * the same brand (after a regeneration) keeps that brand's last good answer on screen until the new one lands, so the
 * cards never blink back to the skeleton; a retry after a failure shows the skeleton. Pure.
 */
export const aboveTheFoldStatus = (
  asked: boolean,
  resolving: boolean,
  key: string,
  brandKey: string,
  last: AboveTheFoldLast | null,
): AboveTheFoldStatus => {
  if (!asked) return resolving ? 'loading' : 'idle';
  if (!last || last.brandKey !== brandKey) return 'loading';
  if (last.key !== key) return last.read.ok ? 'ready' : 'loading';
  return last.read.ok ? 'ready' : 'failed';
};

/**
 * Whether Website Creatives offers the Above the fold sub-tab: while its read is in flight or has failed (the tab
 * holds the skeleton or the error with a retry), and once read, only when it found the brand's cards or a shared part.
 * Pure.
 */
export const aboveTheFoldShows = (status: AboveTheFoldStatus, atf: AboveTheFold | null): boolean =>
  status === 'loading' || status === 'failed' || (status === 'ready' && atf !== null && hasAboveTheFold(atf));

/** {@link aboveTheFoldShows} once the read has answered: something is there, or the read failed (its retry needs the
 *  tab). False while it is in flight, so a tab that may still vanish is not put up over the banners' own state. Pure. */
export const aboveTheFoldSettledShows = (status: AboveTheFoldStatus, atf: AboveTheFold | null): boolean =>
  status !== 'loading' && aboveTheFoldShows(status, atf);

/**
 * The brand's above the fold, read for (brand, site host). `siteHost` is the preview host, which is still being
 * looked up while `resolving`: the read waits for it (the public domain it may hold meanwhile answers 400), and the
 * status stays loading so the section never flashes an error or nothing on the way in.
 */
export const useAboveTheFold = (
  tenantId: number | undefined,
  siteHost: string,
  resolving: boolean,
): AboveTheFoldState => {
  const [attempt, setAttempt] = React.useState(0);
  const [last, setLast] = React.useState<AboveTheFoldLast | null>(null);
  const asked = Boolean(tenantId) && siteHost !== '' && !resolving && creativesServiceConfigured();
  const brandKey = `${tenantId ?? ''}|${siteHost}`;
  const key = `${brandKey}|${attempt}`;

  React.useEffect(() => {
    // Syncs with spark-creatives: reads this brand's above the fold, aborting a read that a newer brand, host or
    // retry has replaced so its late answer never lands on the wrong brand.
    if (!asked || !tenantId) return undefined;
    const abort = new AbortController();
    void fetchAboveTheFold(tenantId, siteHost, abort.signal).then((read) => {
      if (!read.ok && read.aborted) return;
      setLast({ key, brandKey, read });
    });
    return () => abort.abort();
  }, [asked, tenantId, siteHost, key, brandKey]);

  const reload = React.useCallback(() => setAttempt((n) => n + 1), []);
  const status = aboveTheFoldStatus(asked, resolving, key, brandKey, last);
  const atf = status === 'ready' && last?.read.ok ? last.read.value : null;
  return { status, atf, reload };
};
